// My Settings — §9's first section.
//
// "basic profile + preferences; notification preferences; per-user
// communication/contact details Cipher uses (Telegram and similar per-user
// identifiers). All per-user identifiers live here, never scattered across
// Cipher."
//
// PRESERVES, from the v1 `/settings` page:
//   Notification Preferences   GET/PUT /api/operators/me/notification-preferences
//   Onboarding Tour reset      POST    /api/operators/me/onboarding/reset
//
// THE PROFILE BLOCK READS THE SESSION, NOT AN ENDPOINT
// ====================================================
// `GET /auth/me` is already called once by `App.js`'s AuthProvider and its
// result is what the shell hands every surface. Adding a second read of the
// same operator here would be a new request for data already in memory, and
// C5's rule — never a literal identity — is satisfied by using it rather than
// by re-fetching it.
//
// TELEGRAM IS DELIBERATELY NOT EDITABLE HERE TONIGHT — see the block comment
// at `TelegramIdentity` below.
import React, { useEffect, useState } from "react";
import axios from "axios";

import { API, ENDPOINTS, post, put } from "../api";
import { isFounder } from "../sections";
import { Block, Button, Gate, Row, Toggle, Value } from "../ui";
import useResource from "../useResource";

function Profile({ user }) {
  return (
    <Block
      title="Profile"
      note="From your Cipher session. Changing it is not a Settings action today."
    >
      <Row label="Name">
        <Value>{user?.name}</Value>
      </Row>
      <Row label="Email">
        <Value>{user?.email}</Value>
      </Row>
      <Row label="Role" hint="Decides what you see and what the server allows.">
        <Value mono>{user?.role}</Value>
      </Row>
      <Row label="Operator ID">
        <Value mono>{user?.operator_id}</Value>
      </Row>
      <Row
        label="Business unit access"
        hint="Which arms of the business you can operate in."
      >
        <Value>
          {Array.isArray(user?.business_unit_access) && user.business_unit_access.length
            ? user.business_unit_access.join(" · ")
            : null}
        </Value>
      </Row>
    </Block>
  );
}

function NotificationPreferences() {
  const prefs = useResource(ENDPOINTS.notificationPreferences);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");

  useEffect(() => {
    if (prefs.status === "ok" && prefs.data) {
      setDraft(prefs.data.preferences || {});
    }
  }, [prefs.status, prefs.data]);

  // The founder cannot switch alerts off — the server enforces it and says so
  // in the same response. The UI states the reason rather than presenting
  // controls that silently do nothing.
  const locked = prefs.data?.founder_locked;
  const labels = prefs.data?.alert_type_labels || {};

  async function change(key, value) {
    const next = { ...draft, [key]: value };
    setDraft(next);
    setSaving(true);
    setNote("");
    const res = await put(ENDPOINTS.notificationPreferences, next);
    setSaving(false);
    if (res.status !== "ok") {
      setNote("Could not save. Your change has not been applied.");
      setDraft(draft);
      return;
    }
    setNote(res.data?.updated ? "Saved." : res.data?.message || "");
  }

  return (
    <Block
      title="Notification preferences"
      note={
        locked
          ? "The Founder always receives every alert. The server enforces this."
          : "Which alerts Cipher sends you."
      }
    >
      <Gate resource={prefs} empty="No alert types are defined.">
        {() => (
          <>
            {Object.keys(draft || {}).map((key) => (
              <Row key={key} label={labels[key] || key}>
                <Toggle
                  id={`notif-${key}`}
                  checked={draft[key]}
                  disabled={locked || saving}
                  onChange={(v) => change(key, v)}
                  label={draft[key] ? "On" : "Off"}
                />
              </Row>
            ))}
            {note ? (
              <p className="set-note dim" role="status">
                {note}
              </p>
            ) : null}
          </>
        )}
      </Gate>
    </Block>
  );
}

/**
 * Per-user contact identifiers.
 *
 * §9 is emphatic that these live here and nowhere else, so the block exists and
 * says what it is for. It is read-only tonight, and the reason is a rule rather
 * than an omission:
 *
 * The per-user Telegram identifier is written by `POST /api/me/ea/channels/
 * telegram`, which is part of the Personal EA router and is mounted only when
 * `CIPHER_PERSONAL_EA_V1` is on. It is off. §3.9 of the build instruction is
 * explicit — "do not build the EA area on any night when CIPHER_PERSONAL_EA_V1
 * is off, and do not enable it to find out" — and enabling a production flag is
 * outside every session's ceiling (§0, precondition 4).
 *
 * So: the home §9 asks for exists, is named, and is where the control lands the
 * moment the flag is on. What is not done is a second write path to the same
 * identifier built around the flag, which would be exactly the "scattered
 * across Cipher" outcome §9 is written to prevent.
 */
function ContactIdentifiers() {
  return (
    <Block
      title="Contact identifiers"
      note="Per-user identifiers live here and nowhere else in Cipher (§9)."
    >
      <Row
        label="Telegram"
        hint="Set through Customize EA while the Personal EA runtime is off."
      >
        <Value>
          <span className="dim">
            Not configurable here tonight — <code className="mono">CIPHER_PERSONAL_EA_V1</code> is
            off, and the write path for this identifier is behind it.
          </span>
        </Value>
      </Row>
    </Block>
  );
}

const MIN_PASSWORD_LENGTH = 8;

/**
 * Set/change my password — PASSWORD-SET-UI-APPROVED.
 *
 * `POST /auth/set-password` is founder-only (`require_founder`) and, per the
 * ruling, this control is scoped to match: it is rendered only for the
 * Founder, and it only ever sends the logged-in Founder's own email — never
 * an arbitrary email or operator_id, even though the endpoint itself accepts
 * either. This is "set my password," not a general admin tool.
 *
 * The shared `post()` in `../api` collapses a non-2xx response into a
 * generic message and does not carry the backend's `detail` text through, so
 * this calls `axios` directly (the same pattern already used elsewhere in
 * the app, e.g. `ExplorationPhase.jsx`) to surface the real error the server
 * sent rather than a paraphrase of it.
 */
function SetPassword({ user }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [state, setState] = useState("idle"); // idle | working | done | failed
  const [message, setMessage] = useState("");

  const tooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;
  const mismatch = confirm.length > 0 && password !== confirm;
  const canSubmit =
    password.length >= MIN_PASSWORD_LENGTH && password === confirm && state !== "working";

  async function submit() {
    if (password.length < MIN_PASSWORD_LENGTH) {
      setState("failed");
      setMessage(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setState("failed");
      setMessage("Passwords do not match.");
      return;
    }

    setState("working");
    setMessage("");
    try {
      await axios.post(
        `${API}${ENDPOINTS.setPassword}`,
        { email: user?.email, password },
        { withCredentials: true }
      );
      setState("done");
      setMessage("Password updated.");
      setPassword("");
      setConfirm("");
    } catch (err) {
      setState("failed");
      setMessage(err.response?.data?.detail || err.message || "Could not set password.");
    }
  }

  return (
    <Block
      title="Set/change my password"
      note="For the email/password login. Founder-only, and only ever sets your own password."
    >
      <div>
        <Row label="New password" hint={`At least ${MIN_PASSWORD_LENGTH} characters.`}>
          <input
            type="password"
            className="set-input"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            aria-invalid={tooShort}
          />
        </Row>
        <Row label="Confirm password">
          <input
            type="password"
            className="set-input"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            aria-invalid={mismatch}
          />
        </Row>
        {tooShort ? (
          <p className="set-note dim" role="alert">
            Needs at least {MIN_PASSWORD_LENGTH} characters.
          </p>
        ) : null}
        {mismatch ? (
          <p className="set-note dim" role="alert">
            Passwords do not match.
          </p>
        ) : null}
        <span className="set-inline">
          <Button onClick={submit} disabled={!canSubmit}>
            {state === "working" ? "Saving…" : "Set password"}
          </Button>
          {state === "done" ? (
            <span className="dim" role="status">
              {message}
            </span>
          ) : null}
          {state === "failed" ? (
            <span className="dim" role="alert">
              {message}
            </span>
          ) : null}
        </span>
      </div>
    </Block>
  );
}

function OnboardingTour() {
  const [state, setState] = useState("idle");

  async function reset() {
    setState("working");
    const res = await post(ENDPOINTS.onboardingReset, {});
    setState(res.status === "ok" ? "done" : "failed");
  }

  return (
    <Block
      title="Onboarding tour"
      note="Replay the guided tour the next time you open Cipher."
    >
      <Row label="Guided tour" hint="Resets your tour progress. Nothing else changes.">
        <span className="set-inline">
          <Button onClick={reset} disabled={state === "working"}>
            {state === "working" ? "Resetting…" : "Reset tour"}
          </Button>
          {state === "done" ? (
            <span className="dim" role="status">
              Reset. It will run again on your next visit.
            </span>
          ) : null}
          {state === "failed" ? (
            <span className="dim" role="alert">
              Could not reset.
            </span>
          ) : null}
        </span>
      </Row>
    </Block>
  );
}

export default function MySettings({ user }) {
  return (
    <>
      <Profile user={user} />
      {isFounder(user) ? <SetPassword user={user} /> : null}
      <NotificationPreferences />
      <ContactIdentifiers />
      <OnboardingTour />
    </>
  );
}
